// ============================================
// COTIZADORCONFIG.JS — Configuración global del cotizador
// ============================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloTarjeta,
} from './components/helpersClientes';
import { authHeaders } from './utils/storage';

const CAMPOS = [
  {
    clave: 'descuento_syscom_global',
    label: 'Descuento global sobre Syscom',
    sufijo: '%',
    ayuda: 'Aplicado a TODOS los productos Syscom para calcular tu costo Beep (precio_lista · (1 - descuento)).',
    seccion: 'descuentos',
  },
  {
    clave: 'umbral_margen_seguro',
    label: 'Margen seguro (🟢)',
    sufijo: '% o más',
    ayuda: 'Margen mínimo considerado seguro en la vigilancia.',
    seccion: 'umbrales',
  },
  {
    clave: 'umbral_margen_vigilar',
    label: 'Margen a vigilar (🟡)',
    sufijo: '% o más',
    ayuda: 'Bajo este umbral la vigilancia marca 🔴 alerta.',
    seccion: 'umbrales',
  },
  {
    clave: 'umbral_costo_syscom',
    label: 'Alertar si costo Syscom sube más de',
    sufijo: '%',
    ayuda: 'Variación porcentual del precio origen respecto al snapshot.',
    seccion: 'vigilancia',
  },
  {
    clave: 'umbral_tc_cambio',
    label: 'Alertar si TC cambia más de',
    sufijo: '%',
    ayuda: 'Variación porcentual del TC actual respecto al TC del snapshot.',
    seccion: 'vigilancia',
  },
];

export default function CotizadorConfig() {
  const [config, setConfig] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);
  const [toast, setToast] = useState(null);

  const mostrarToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizador/config`).then(x => x.json());
      if (r.ok) {
        const init = {};
        for (const c of CAMPOS) {
          init[c.clave] = r.config[c.clave] != null ? String(r.config[c.clave]) : '';
        }
        setConfig(init);
        setSucio(false);
      }
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => {
    setConfig(c => ({ ...c, [k]: v }));
    setSucio(true);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      // Convertimos a números donde aplique
      const body = {};
      for (const c of CAMPOS) {
        const n = Number(config[c.clave]);
        if (!Number.isFinite(n)) throw new Error(`${c.label} inválido`);
        body[c.clave] = n;
      }
      const r = await fetch(`${API_BASE_CLI}/api/cotizador/config`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      mostrarToast(`✅ ${r.actualizados} valor(es) guardados`);
      setSucio(false);
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally { setGuardando(false); }
  };

  const secciones = [
    { id: 'descuentos', icono: '💰', titulo: 'Descuentos' },
    { id: 'umbrales',   icono: '🎯', titulo: 'Umbrales de alerta (listas pactadas)' },
    { id: 'vigilancia', icono: '📊', titulo: 'Vigilancia automática' },
  ];

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>
          ⚙️ Configuración del cotizador
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>
          Descuento global sobre Syscom y umbrales de vigilancia para listas pactadas.
        </div>
      </div>

      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      {!cargando && secciones.map(sec => (
        <div key={sec.id} style={{ ...estiloTarjeta, padding: 20, marginBottom: 14 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: COLOR_OSCURO,
            textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 14,
            paddingBottom: 10, borderBottom: `2px solid ${COLOR_BORDE}`,
          }}>
            {sec.icono} {sec.titulo}
          </div>
          {CAMPOS.filter(c => c.seccion === sec.id).map(c => (
            <div key={c.clave} style={{
              display: 'grid', gridTemplateColumns: '1fr 180px', gap: 14,
              alignItems: 'center', marginBottom: 14,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>{c.label}</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2, lineHeight: 1.4 }}>
                  {c.ayuda}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...estiloInput, paddingRight: 60, textAlign: 'right', fontSize: 14, fontWeight: 600 }}
                  type="number" step="0.1" min="0"
                  value={config[c.clave] || ''}
                  onChange={(e) => set(c.clave, e.target.value)}
                />
                <span style={{
                  position: 'absolute', right: 10, top: 9,
                  color: COLOR_GRIS_CLARO, fontSize: 12,
                }}>{c.sufijo}</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      {!cargando && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            style={{ ...estiloBotonPrimario, background: '#1D9E75', opacity: !sucio || guardando ? 0.5 : 1 }}
            disabled={!sucio || guardando}
            onClick={guardar}
          >
            {guardando ? 'Guardando…' : '💾 Guardar cambios'}
          </button>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}
